import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, ExternalLink, FileText, Image as ImageIcon, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@components/ui/Button'
import MarkdownRenderer from '@/components/content/MarkdownRenderer'
import type { ChatFilePanelTarget } from '@/store/operate'

interface ChatFilePreviewPanelProps {
  file: ChatFilePanelTarget
  onClose: () => void
}

function formatFileSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getExtension(filename?: string) {
  return filename?.split('.').pop()?.toLowerCase() || ''
}

function isImageFile(filename?: string) {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(
    getExtension(filename),
  )
}

function isMarkdownFile(filename?: string) {
  return ['md', 'markdown'].includes(getExtension(filename))
}

function isJsonFile(filename?: string) {
  return getExtension(filename) === 'json'
}

function isTextFile(filename?: string) {
  return [
    'txt',
    'md',
    'markdown',
    'json',
    'yaml',
    'yml',
    'toml',
    'xml',
    'csv',
    'log',
    'js',
    'ts',
    'tsx',
    'jsx',
    'py',
    'go',
    'java',
    'rb',
    'php',
    'rs',
    'sh',
    'sql',
    'css',
    'scss',
    'html',
  ].includes(getExtension(filename))
}

function prettifyJson(content: string) {
  try {
    return JSON.stringify(JSON.parse(content), null, 2)
  } catch {
    return content
  }
}

function shouldHidePreviewError(message: string | null) {
  if (!message) return false
  const normalized = message.trim().toLowerCase()
  return normalized === 'failed to fetch' || normalized.includes('load failed')
}

export default function ChatFilePreviewPanel({
  file,
  onClose,
}: ChatFilePreviewPanelProps) {
  const { t } = useTranslation()
  const [content, setContent] = useState<string | null>(null)
  const [contentError, setContentError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [useIframeFallback, setUseIframeFallback] = useState(false)
  const contentCacheRef = useRef<Map<string, { content?: string; error?: string }>>(new Map())
  const fileUrl = file.fileUrl || file.url || null
  const textFile = isTextFile(file.filename)
  const imageFile = isImageFile(file.filename)
  const markdownFile = isMarkdownFile(file.filename)
  const jsonFile = isJsonFile(file.filename)
  const fileSize = formatFileSize(file.size)

  useEffect(() => {
    setContent(null)
    setContentError(null)
    setUseIframeFallback(false)

    if (!fileUrl || imageFile || !textFile) {
      setIsLoading(false)
      return
    }

    const cached = contentCacheRef.current.get(fileUrl)
    if (cached) {
      setContent(cached.content ?? null)
      setContentError(cached.error ?? null)
      setUseIframeFallback(Boolean(cached.error))
      setIsLoading(false)
      return
    }

    const controller = new AbortController()
    const load = async () => {
      setIsLoading(true)
      try {
        const headers: HeadersInit = {}
        const token = localStorage.getItem('auth_token')
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
        const response = await fetch(fileUrl, {
          signal: controller.signal,
          headers,
        })
        if (!response.ok) {
          throw new Error(`Unable to load file (${response.status})`)
        }
        const text = await response.text()
        contentCacheRef.current.set(fileUrl, { content: text })
        setContent(text)
        setContentError(null)
      } catch (error) {
        if (controller.signal.aborted) return
        const message = error instanceof Error ? error.message : 'Unable to preview file'
        contentCacheRef.current.set(fileUrl, { error: message })
        setContent(null)
        setContentError(message)
        setUseIframeFallback(true)
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    void load()
    return () => controller.abort()
  }, [fileUrl, imageFile, textFile])

  const renderedContent = useMemo(() => {
    if (!content) return null
    return jsonFile ? prettifyJson(content) : content
  }, [content, jsonFile])
  const visiblePreviewError = shouldHidePreviewError(contentError) ? null : contentError

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {imageFile ? (
                <ImageIcon className="h-4 w-4 text-primary" />
              ) : (
                <FileText className="h-4 w-4 text-primary" />
              )}
              <h3 className="truncate text-sm font-semibold">{file.filename}</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {imageFile ? t('filePreview.image') : textFile ? t('filePreview.document') : t('filePreview.file')}
              {fileSize ? ` • ${fileSize}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {fileUrl && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('common.open')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t('common.download')}
                </Button>
              </>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden bg-background">
        {!fileUrl ? (
          <div className="m-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            {t('filePreview.noPreviewUrl')}
          </div>
        ) : imageFile ? (
          <div className="flex h-full items-center justify-center p-4">
            <img
              src={fileUrl}
              alt={file.filename}
              className="max-h-full max-w-full rounded-xl object-contain shadow-sm"
            />
          </div>
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('filePreview.loadingPreview')}
          </div>
        ) : renderedContent && markdownFile ? (
          <div className="h-full overflow-auto px-5 py-4">
            <article className="prose prose-sm max-w-none dark:prose-invert">
              <MarkdownRenderer content={renderedContent} fallback={<pre>{renderedContent}</pre>} />
            </article>
          </div>
        ) : renderedContent ? (
          <pre className="h-full overflow-auto border-0 bg-card px-4 py-4 font-mono text-xs leading-relaxed text-foreground">
            <code>{renderedContent}</code>
          </pre>
        ) : useIframeFallback ? (
          <div className="flex h-full flex-col">
            {visiblePreviewError && (
              <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                {visiblePreviewError}
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-hidden border-t border-border bg-card">
              <iframe
                src={fileUrl}
                title={file.filename}
                className="h-full w-full"
              />
            </div>
          </div>
        ) : (
          <div className="m-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            {t('filePreview.noInlinePreview')}
          </div>
        )}
      </div>
    </div>
  )
}
